import { Layer } from "../components/MapDrawer";
import { LayerGroupTitle, LayerTitle } from "../types/layers";
import { Accordion, MapDataTitle, MapSettings } from "../types/map";

// Almost the same, but arrays are changed to just the type
type SettingsToUpdate = {
  [K in keyof MapSettings]: MapSettings[K] extends Array<infer ArrayT>
    ? ArrayT
    : MapSettings[K];
};

type SettingToReturn<K extends keyof MapSettings> =
  MapSettings[K] extends Array<infer ArrayT> ? ArrayT : MapSettings[K];

// Layer initial settings are defined in layer.ts
const InitialSettings: MapSettings = {
  layers: [],
  open: false,
  cesiumDrawerOpen: false,
  cesiumDrawerPinned: true,
  pinned: true,
  accordions: [],
  drawerTab: "data",
  cesiumDrawerTab: "data",
  featuresInCurrentView: false,
  atonAlertCircle: true,
  atonOnlyFaulted: false,
  atonOnlyRemote: false,
  surveyOutlines: true,
  iceLegend: true,
};

const updateMapSettings = (newSettings: Partial<SettingsToUpdate>): void => {
  const oldSettings = localStorage.getItem("mapSettings");
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const settingsToSave: MapSettings = oldSettings
    ? JSON.parse(oldSettings)
    : InitialSettings;
  // Loop through the keys and update if key is provided
  const settingKeys = Object.keys(InitialSettings) as (keyof MapSettings)[];
  settingKeys.forEach((k) => {
    const currentSetting = settingsToSave[k];
    const newSetting = newSettings[k];
    if (Array.isArray(currentSetting) && typeof newSetting === "object") {
      // Filter-method needs this. Hopefully solved in later versions of TS
      const cur = currentSetting as (Accordion | Layer)[];
      const filtered = cur.filter((x) => x.title !== newSetting.title);
      (settingsToSave[k] as typeof cur) = newSetting
        ? [...filtered, newSetting]
        : currentSetting;
    } else if (
      !Array.isArray(currentSetting) &&
      typeof newSetting !== "object"
    ) {
      (settingsToSave[k] as typeof currentSetting) =
        newSetting ?? currentSetting ?? InitialSettings[k];
    }
  });
  localStorage.setItem("mapSettings", JSON.stringify(settingsToSave));
};

function readMapSettings<K extends keyof MapSettings>(
  key: K,
  title?: LayerGroupTitle | MapDataTitle | LayerTitle | string,
): SettingToReturn<K> {
  const localSettings = localStorage.getItem("mapSettings");
  const parsedSettings = localSettings
    ? (JSON.parse(localSettings) as MapSettings)
    : InitialSettings;
  const setting = parsedSettings[key];
  if (Array.isArray(setting) && title) {
    // Filter-method needs this. Hopefully solved in later versions of TS
    const typedSettings = setting as (Accordion | Layer)[];
    const filtered = typedSettings.filter((x) => x.title === title)[0];
    return filtered as SettingToReturn<K>;
  }
  return parsedSettings[key] as SettingToReturn<K>;
}

export { updateMapSettings, readMapSettings };
